import type {
  BlockType,
  DisplayConditionSources,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  ExperienceTheme,
  ExperienceVariable,
  RichText,
} from '@matter_extractor/shared';

/**
 * The seam of the READER (refonte modulaire §9.2, §9.3, §9.4; maquette
 * `Lecteur.dc.html`, `EditeurPageEcran.dc.html`, `EditeurPageTelephone.dc.html`,
 * `ModeAnimation.dc.html`).
 *
 * One component plays a page, and FOUR surfaces mount it: the preview of the
 * page editor (§9.3), the scene of the Timeline (§9.1), the full-screen preview
 * of #141, and the session itself (§9.5). That is the whole reason it is a
 * component rather than a screen - « le rendu doit être le même dans l'éditeur
 * et dans la session », and a page painted twice would be two spellings of one
 * experience. The fourth arrived with the only prop pair below that takes
 * something AWAY from the reader (`revealed` / `onRevealed`), and it is the
 * reason that pair is a pair: the three older mounts pass neither and are
 * unchanged.
 *
 * This file is the agreement between the four, like `protocoles/contracts.ts`
 * is between the three hands of the protocol editor: the props, the shapes they
 * carry, and every `data-testid` more than one file reads. The reader is
 * CONTROLLED end to end - it holds no value, fetches nothing, and asks for
 * every change through a callback.
 */

/* ----------------------------------------------------------------- the axes */

/**
 * The two formats of §9.3 (« l'aperçu au centre sur le vrai lecteur au format
 * téléphone et au format écran »).
 *
 * A format is NOT a breakpoint: it is the shape the page is authored for, and
 * the editor switches it by hand. It settles the reading column, the type steps
 * and the rhythm - the numbers come from the two mockup plates.
 */
export type ReaderFormat = 'phone' | 'screen';

/**
 * The three modes.
 *
 * - `static` - nothing reacts and everything is visible at once: the scene of
 *   the Timeline, which shows a page rather than plays it.
 * - `interactive` - hover, typing and clicks as in the session; the temps play
 *   from the mount, a button advances, a page with an automatic duration
 *   advances on its own.
 * - `edit` - the page is whole, a click on a block's visual selects it, and
 *   nothing inside a block reacts: the « Sélection par l'aperçu » switch of
 *   §9.3 is what moves the editor's preview between `edit` and `interactive`.
 */
export type ReaderMode = 'static' | 'interactive' | 'edit';

/** What a block holds: the text of an Input, the tick of a Checkbox. */
export type ReaderValue = string | boolean;

/**
 * Every value of the page, keyed by BLOCK id.
 *
 * By block id and never by variable name: §8.6 is explicit that the name is a
 * label and the token carries the id, so a reader keyed by name would break the
 * moment somebody renames a field - which §8.6 promises is free.
 */
export type ReaderValues = Readonly<Record<string, ReaderValue>>;

/** A Bouton, narrowed - what `onAdvance` hands back so the caller can write `{{choix:<pageId>}}`. */
export type ExperienceButtonBlock = Extract<ExperienceBlockDraft, { type: 'button' }>;

/** A Noyau block, narrowed - the ninth type of §9.2 (noyau.md §3.2). */
export type ExperienceNoyauBlock = Extract<ExperienceBlockDraft, { type: 'noyau' }>;

/**
 * Why the page is over.
 *
 * `button` carries the Bouton that was pressed, because the caller needs its
 * `label`, its `rememberChoice` and its `variableName` to write the choice of
 * §8.6; `duration` is the optional `autoDurationSeconds` of §9.2 running out,
 * and it carries no block - nobody pressed anything; `noyau` is « Choisir ce
 * texte » under the two cards of a Noyau block (noyau.md §5.1), carrying the
 * block and the OPAQUE identifier of the card confirmed - the session posts it,
 * a preview reads it back against its own cards.
 */
export type ReaderAdvance =
  | { readonly reason: 'button'; readonly block: ExperienceButtonBlock }
  | { readonly reason: 'duration'; readonly block: null }
  | { readonly reason: 'noyau'; readonly block: ExperienceNoyauBlock; readonly cardId: string };

/**
 * One card of a choice as the reader draws it: an identifier and a rich text,
 * and nothing else (noyau.md §5.1, N19, N28). The session hands the opaque
 * identifiers its view served; a preview hands its own.
 */
export type ReaderNoyauCard = {
  readonly cardId: string;
  readonly text: RichText;
};

/**
 * What the Noyau blocks of the page SHOW (noyau.md §3.2, §5; N20) - handed by
 * the caller like every value, since the reader fetches nothing.
 *
 * - `choices` - the two cards of each block of choice, by the block's id, in
 *   the order to draw them; a block absent here draws two empty frames, which
 *   only a surface that knows nothing of the texts ever shows (the scene of the
 *   Timeline, a vignette);
 * - `portrait` - what a block in portrait mode shows, or null for the same
 *   empty frame.
 *
 * The session builds it from its view (`noyau.choice`, `noyau.portrait`), the
 * two previews from the REAL texts of a pair of example (N20). The selected
 * card is a VALUE like any other - `values[block.id]`, the card's identifier -
 * so the reader stays controlled: the session, the editor's preview and the
 * full-screen preview each hold it where they hold what is typed.
 */
export type ReaderNoyau = {
  readonly choices: Readonly<Record<string, readonly [ReaderNoyauCard, ReaderNoyauCard]>>;
  readonly portrait: RichText | null;
};

/* ---------------------------------------------------------------- the props */

export interface PageReaderProps {
  /**
   * The page to play. Typed as the DRAFT shape because the editor holds one;
   * an `ExperiencePage` of a saved version is structurally assignable to it, so
   * the session hands its own without a conversion.
   */
  readonly page: ExperiencePageDraft;
  /**
   * The experience's theme - PARTIAL, as stored (§9.4). The reader merges it
   * with the page's own `visual` and with each block's, so the caller never
   * resolves anything itself.
   */
  readonly theme: ExperienceTheme;
  readonly format: ReaderFormat;
  readonly mode: ReaderMode;
  /** What the person has typed and ticked so far. Keyed by block id. */
  readonly values: ReaderValues;
  /** `edit` only: the block drawn with the navy frame and its type tab. */
  readonly selectedBlockId?: string | null;
  /** `edit` only: a click on a block's visual. */
  readonly onSelectBlock?: (blockId: string) => void;
  /** `interactive` only: an Input typed into, a Checkbox ticked. */
  readonly onValueChange?: (blockId: string, value: ReaderValue) => void;
  /** `interactive` only: the page is over - a button was pressed, or the duration ran out. */
  readonly onAdvance?: (advance: ReaderAdvance) => void;
  /**
   * Change it to replay the appearance (« Rejouer l'apparition », §9.3).
   *
   * In `interactive` the appearance plays on the mount as well; in `edit` the
   * page is whole on the mount and only a CHANGE of this key plays it; in
   * `static` it never plays. Changing it remounts the body, so a temps that had
   * already appeared animates again.
   */
  readonly replayKey?: string | number;
  /**
   * What a variable renders as, at the moment the text is served (§8.6).
   *
   * Absent - the editor's preview - every token renders as its stored spelling,
   * which is the régime `renderRichText` documents and what the inspector's
   * chip stands for. The session passes its own, because a `{{champ:...}}` of a
   * previous page is not in this page's `values`.
   */
  readonly resolveVariable?: (variable: ExperienceVariable) => string | null;
  /**
   * What a variable is NAMED, for the previews of the editor (§8.6; grill
   * Variables of 2026-09-16, D4, D5).
   *
   * « L'aperçu d'une page la peint dans les couleurs du thème et montre le nom,
   * jamais une valeur inventée. » Given, every token is drawn as a pill in the
   * theme's accent carrying the label this answers - « @prénom », or
   * « @objectif inconnu » for a token nothing resolves. The session never passes
   * it: it resolves. A reader given neither shows the stored spelling, as before.
   *
   * A reader given BOTH resolves TOKEN BY TOKEN: `resolveVariable` answers a
   * value, or `null`, and the pill is drawn for every `null`. The full-screen
   * preview of #141 is the one surface that does it, and that is the whole of
   * its exception to D5 - a field it resolves was TYPED, on a previous page, by
   * the person looking, so nothing is invented - while an objective, a
   * criterion and an empty field keep the pill that says « ceci n'est pas du
   * contenu ». What tells the SESSION apart is that it passes no namer at all,
   * never that it passes a resolver.
   */
  readonly variableNames?: (variable: ExperienceVariable) => string;
  /**
   * How many temps are shown - CONTROLLED, with `onRevealed` (§9.3; issue #141).
   *
   * The standard React controlled / uncontrolled pair, and it is the ONE thing
   * the full-screen preview needed the reader to give up. Absent - the preview
   * of the page editor, the scene of the Timeline, the session - the reader
   * owns the count exactly as it always has, and not one of their behaviours
   * moves. Given, the CALLER owns it, and the reader still owns everything
   * else: it alone knows WHEN one more temps is due - a delay that ran out, a
   * Bouton `next-temps` that was pressed - and it asks through `onRevealed`
   * instead of moving its own state.
   *
   * That split is the whole point. The full-screen preview must be able to
   * reveal a temps the page itself gates behind a click or a delay, and it says
   * « un de plus » without knowing a thing about how a temps appears.
   *
   * It is counted like the internal state it replaces: 1 is « the first temps
   * is on screen », 0 is a page with no temps at all. The reader still clamps
   * it to `page.temps.length`, so a caller cannot reveal past the page.
   */
  readonly revealed?: number;
  /**
   * The reader asking for `revealed` to become this. Controlled pairs only -
   * a reader that owns its count never calls it.
   */
  readonly onRevealed?: (revealed: number) => void;
  /**
   * What the display conditions of §9.2 are evaluated on (H70, H72), with
   * `isBlockDisplayed` of `shared` - the very function the server plays at the
   * advance, so the page the person sees and the page the server judges are
   * one page.
   *
   * Absent, every block shows: the editor's « Montrer tous les blocs » (H73),
   * the scene of the Timeline, and every mount that predates the conditions.
   * Given, a block whose conditions do not hold does not exist for the page -
   * it is not drawn, does not animate, greys no button - and a temps every
   * block of which is masked is skipped, its delay included. The session hands
   * the values it holds; the editor its simulation; the full-screen preview what
   * the person looking has typed and chosen.
   */
  readonly conditions?: DisplayConditionSources;
  /**
   * The editor previews only (H73): the mark of a CONDITIONAL block, « si
   * @noyau · SGP », or null for a block that carries no condition. Given, every
   * conditional block on screen wears it in the grey of the admin; absent, no
   * block does - a person never sees a mark.
   */
  readonly conditionMark?: (block: ExperienceBlockDraft) => string | null;
  /**
   * What the Noyau blocks of the page show - their cards and the portrait
   * (noyau.md §5, issue #151). Absent, a Noyau block draws its empty frames:
   * the scene of the Timeline and the vignettes, which read no text.
   */
  readonly noyau?: ReaderNoyau;
  /**
   * The Bouton whose advance WAITS (noyau.md §4.6, N87; issue #174), or null:
   * the Bouton pressed, from the moment its advance leaves - the request, then,
   * on a refusal `noyau-pending`, the determination of the noyau - until the
   * session moves or refuses. It says its waiting label, `READER_WAITING_LABEL`
   * when it has none, and stays a real, disabled button; the other buttons of
   * the page keep their words. The session alone passes it: no preview ever
   * shows a wait (N89).
   */
  readonly waitingBlockId?: string | null;
  /**
   * The phrase of thanks of the feedback page, once the feedback is SENT
   * (retour-experience.md §5; issue #176): given, it takes the place of the
   * page's blocks - painted at the theme's text style, centred in the height of
   * the window, with no button - and nothing else of the page is drawn. The
   * session alone passes it, from `feedbackThanksOf` when the view says the
   * feedback was sent; the previews of the editor never do, and the page they
   * show stays whole. Absent or null, the page is the page.
   */
  readonly thanks?: string | null;
}

/**
 * « Un instant... » - what a button whose advance is in flight or waits says
 * when nothing else is set (noyau.md §4.6, N83): the protocol page's
 * « Continuer » always (F3), and a Bouton whose « Libellé d'attente » is empty -
 * the default, and every Bouton stored before #174. The inspector shows it as
 * the grey text of that empty field.
 */
export const READER_WAITING_LABEL = 'Un instant...';

/* ---------------------------------------------------------------- the marks */

/**
 * Every `data-testid` of the reader that more than one file reads.
 *
 * Prefixed `lecteur-`, one register, for the same reason the versioned layer
 * puts its prefixes in `VersionedWording`: the capture scripts and the suites
 * of #57 and #58 read them, and a mark spelled in two components is a selector
 * that resolves in one and not the other.
 *
 * A block is found by ONE mark plus its `data-block-type` / `data-block-id`
 * attributes rather than six marks, because every consumer wants « the block at
 * this id » and none wants « all the images ».
 */
export const READER_TEST_IDS = {
  /** The reader's root - what carries the theme's custom properties. */
  root: 'lecteur',
  /** One group per temps, with `data-temps-index`. Only the appeared ones exist. */
  temps: 'lecteur-temps',
  /** One per block, with `data-block-id`, `data-block-type` and `data-selected`. */
  block: 'lecteur-bloc',
  /** `edit` only: the transparent target that takes the click and names the block. */
  blockTarget: 'lecteur-bloc-cible',
  /** `edit` only: the type tab of the selected block. */
  blockTab: 'lecteur-bloc-onglet',
  /** The sentence under a blocked button - why it is grey (§9.2, H30). */
  advanceHint: 'lecteur-aide',
  /** A Son's play control, shown until the first gesture has let it start. */
  soundPlay: 'lecteur-son-jouer',
  /** `edit` only: an Image or a Son whose file has not been picked yet. */
  emptyAsset: 'lecteur-bloc-vide',
  /** The editor previews only: the grey mark of a conditional block (§9.2, H73). */
  conditionMark: 'lecteur-bloc-condition',
  /** A variable drawn as a pill in the theme's accent - the previews only (D4). */
  variable: 'lecteur-variable',
  /** Le bouton de copie d'un Texte copiable (§9.2, issue #137). */
  copy: 'lecteur-copier',
  /**
   * Ce que la copie a répondu : la pastille « Copié », ou le refus explicite.
   *
   * ONE mark for the two answers, with `data-outcome` telling them apart, for
   * the reason the block mark gives above: every consumer wants « what did the
   * copy say », none wants « all the failures ».
   */
  copyOutcome: 'lecteur-copier-retour',
  /**
   * Le bloc Noyau (noyau.md §5.1, issue #151) : le groupe des deux cartes, une
   * carte - avec `data-card-id` et `data-selected` -, le bouton « Choisir ce
   * texte » et le portrait. Une carte se trouve par sa marque et son
   * identifiant, jamais par son texte : la suite et les captures jouent des
   * textes fictifs qu'elles ne relisent pas.
   */
  noyauCards: 'lecteur-noyau-cartes',
  noyauCard: 'lecteur-noyau-carte',
  noyauConfirm: 'lecteur-noyau-choisir',
  noyauPortrait: 'lecteur-noyau-portrait',
  /**
   * Le bloc Sélection (retour-experience.md §4.1, issue #176) : le groupe de
   * pastilles - un `radiogroup` - et une pastille, avec `aria-checked`. Une
   * pastille se trouve par son libellé de rôle, jamais par un indice : l'ordre
   * des options est celui que l'opérateur a écrit.
   */
  selection: 'lecteur-selection',
  selectionOption: 'lecteur-selection-option',
  /**
   * Le bloc Note (§4.2) : le groupe des cinq étoiles - un `radiogroup` - et une
   * étoile, avec `aria-checked` et `data-state` (`on`, `hover` ou absent).
   */
  note: 'lecteur-note',
  noteStar: 'lecteur-note-etoile',
  /** La phrase de remerciement qui remplace les blocs de la page de retour (§5). */
  thanks: 'lecteur-merci',
  /**
   * Les éléments d'une page jouée dans la conversation (prolongation.md §5),
   * sous sa bulle : le groupe, avec `data-mode` (`live` ou `kept`), et l'une des
   * deux cartes fantômes qui tiennent la place des cartes tant que le noyau
   * n'est pas rendu. Les blocs du groupe gardent leurs marques ordinaires
   * (`block`, `noyauCard`...) : c'est le même lecteur.
   */
  threadItems: 'lecteur-fil-elements',
  threadGhost: 'lecteur-fil-fantome',
} as const;

/**
 * How a type of block is NAMED on screen - the type tab of the selected block,
 * and the name of its selection target.
 *
 * The mockup writes them this way in the pile (« Texte · Avant de commencer »,
 * « Input · Votre prénom »), and #58 spends the same table in its own list: one
 * vocabulary, so an operator reads one word for one thing on both surfaces.
 */
export const READER_BLOCK_LABEL: Readonly<Record<BlockType, string>> = {
  text: 'Texte',
  image: 'Image',
  sound: 'Son',
  button: 'Bouton',
  input: 'Input',
  checkbox: 'Checkbox',
  link: 'Lien',
  copyable: 'Texte copiable',
  noyau: 'Noyau',
  selection: 'Sélection',
  note: 'Note',
};
